import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, RotateCw, Square, Trash2, Zap } from "lucide-react";
import {
  useDeleteRun,
  useRebaseRun,
  useStartRun,
  useStopRun,
} from "@/api/runs";
import type { Run } from "@/api/types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface Props {
  run: Run;
  canMutate: boolean;
  mutationReason: string | null;
}

export function RunRowActions({ run, canMutate, mutationReason }: Props) {
  const navigate = useNavigate();
  const stop = useStopRun();
  const rebase = useRebaseRun();
  const startRun = useStartRun();
  const del = useDeleteRun();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isActive = run.status === "running" || run.status === "idle";
  const canRebase = run.status === "idle";
  const canDelete =
    canMutate && run.status !== "running" && run.status !== "pending" && !del.isPending;

  async function onRerun() {
    if (!canMutate) return;
    const next = await startRun.mutateAsync({
      scenario_id: run.scenario_id,
      backend_id: run.backend_id,
      template_bindings: run.template_bindings,
    });
    navigate(`/runs/${next.id}`);
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex items-center justify-end gap-1">
        <IconButton
          tooltip="Open run"
          onClick={() => navigate(`/runs/${run.id}`)}
          icon={<ExternalLink size={14} />}
        />
        <IconButton
          tooltip={
            !canMutate
              ? mutationReason ?? "Insufficient permission"
              : canRebase
                ? "Rebase to now"
                : "Rebase needs status=idle"
          }
          disabled={!canMutate || !canRebase || rebase.isPending}
          onClick={() => rebase.mutate(run.id)}
          icon={<RotateCw size={14} />}
        />
        <IconButton
          tooltip={
            !canMutate
              ? mutationReason ?? "Insufficient permission"
              : isActive
                ? "Stop run"
                : "Already terminated"
          }
          disabled={!canMutate || !isActive || stop.isPending}
          onClick={() => stop.mutate(run.id)}
          icon={<Square size={14} />}
        />
        <IconButton
          tooltip={
            !canMutate
              ? mutationReason ?? "Insufficient permission"
              : "Start a new run with the same scenario, backend, and noise"
          }
          disabled={!canMutate || startRun.isPending}
          onClick={onRerun}
          icon={<Zap size={14} />}
        />
        <IconButton
          tooltip={
            !canMutate
              ? mutationReason ?? "Insufficient permission"
              : canDelete
              ? "Delete run"
              : "Stop the run before deleting"
          }
          disabled={!canDelete}
          onClick={() => setConfirmDelete(true)}
          icon={<Trash2 size={14} />}
          danger
        />
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this run?</AlertDialogTitle>
            <AlertDialogDescription>
              Removes <span className="font-mono">{run.id}</span> from history
              and deletes its generated bundle from disk. Telemetry already
              pushed to the backend stays in the backend.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => del.mutate(run.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
}

function IconButton({
  tooltip,
  onClick,
  disabled,
  icon,
  danger,
}: {
  tooltip: string;
  onClick: () => void;
  disabled?: boolean;
  icon: React.ReactNode;
  danger?: boolean;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          className={
            danger
              ? "h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
              : "h-8 w-8"
          }
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          aria-label={tooltip}
        >
          {icon}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}
